import React from "react";
import { Award } from "lucide-react";
import type { EquippedCosmeticItem } from "../api/profile";
import { getBadgeAsset, getFrameAsset } from "../lib/cosmetics";
import { rarityGlowSm } from "../lib/rarity";
import { titleColorHex } from "@atlas/shared";

// ─── ProfileIdentity ──────────────────────────────────────────────────────────
// THE single render of "who is this" — avatar (+ optional equipped frame ring)
// + name + equipped title + up to 3 badge chips. Data-in only (no fetching):
// pass whatever a page already has from GET /profile/stats' resolved
// `equipped` shape, or the equivalent for a public profile / social post
// author later (05-social reuses this component).

function titleColorVar(token: string | undefined, hex?: string): string {
  switch (token) {
    case "sky":
      return "var(--sky-400)";
    case "spruce":
      return "var(--spruce-400)";
    case "ember":
      return "var(--coral-400)";
    case "proof":
      return "var(--proof-400)";
    default:
      // A token added after this build: the item carries its own colour.
      return titleColorHex(hex) ?? "var(--gold-400)";
  }
}

const AVATAR_SIZES = { sm: 32, md: 48, lg: 72 } as const;
const BADGE_SIZES = { sm: 16, md: 20, lg: 26 } as const;

function Avatar({
  name,
  avatarUrl,
  size,
}: {
  name: string;
  avatarUrl?: string | null;
  size: number;
}) {
  const initials =
    name
      .split(" ")
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";
  const fontSize = Math.round(size * 0.38);

  return (
    <div
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: "50%",
        overflow: "hidden",
        border: "2px solid var(--accent)",
        background: "var(--surface-raised)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "var(--font-display)",
        fontWeight: 700,
        fontSize,
        color: "var(--text-accent)",
      }}
    >
      {avatarUrl ? (
        <img src={avatarUrl} alt={name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        initials
      )}
    </div>
  );
}

function BadgeChip({ badge, size }: { badge: EquippedCosmeticItem; size: number }) {
  const src = getBadgeAsset(badge.metadata.assetKey);
  const glow = rarityGlowSm(badge.rarity);
  return (
    <span
      title={glow ? `${badge.name} · ${badge.rarity}` : badge.name}
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--surface-raised)",
        border: "1px solid var(--border-subtle)",
        boxShadow: glow,
        overflow: "hidden",
      }}
    >
      {src ? (
        <img src={src} alt="" width={size} height={size} style={{ display: "block" }} />
      ) : (
        <Award size={Math.round(size * 0.6)} style={{ color: "var(--gold-400)" }} />
      )}
    </span>
  );
}

// Equipped frame — an SVG ring overlaid on the avatar (or avatarSlot). Drawn
// slightly oversize so rope/laurel artwork sits on the avatar's rim. Centred
// on the slot with a transform so it stays aligned whatever the slot renders.
function FrameOverlay({ frame, avatarSize }: { frame: EquippedCosmeticItem; avatarSize: number }) {
  const src = getFrameAsset(frame.metadata.assetKey);
  if (!src) return null;
  const frameSize = Math.round(avatarSize * 1.22);
  return (
    <img
      src={src}
      alt=""
      title={frame.name}
      width={frameSize}
      height={frameSize}
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        // Global img max-width:100% would clamp the oversize ring to the
        // avatar wrapper's width — explicit size keeps the rim overlap.
        width: frameSize,
        height: frameSize,
        maxWidth: "none",
        pointerEvents: "none",
      }}
    />
  );
}

export interface ProfileIdentityProps {
  name: string;
  avatarUrl?: string | null;
  title?: EquippedCosmeticItem | null;
  badges?: EquippedCosmeticItem[];
  /** Equipped avatar frame (store `frame` cosmetic). */
  frame?: EquippedCosmeticItem | null;
  size?: "sm" | "md" | "lg";
  /** Shows email/subtitle under the name (ProfilePage-style). */
  subtitle?: React.ReactNode;
  /** Override the default circular avatar (e.g. ProfilePage's upload button). */
  avatarSlot?: React.ReactNode;
}

export function ProfileIdentity({
  name,
  avatarUrl,
  title = null,
  badges = [],
  frame = null,
  size = "md",
  subtitle,
  avatarSlot,
}: ProfileIdentityProps) {
  const avatarSize = AVATAR_SIZES[size];
  const badgeSize = BADGE_SIZES[size];
  const titleText = title?.metadata.text ?? title?.name;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: size === "sm" ? 10 : 14, minWidth: 0 }}>
      {/* display:flex kills the inline-flex baseline gap an avatarSlot would
          otherwise introduce, so the frame overlay centres on the avatar. */}
      <div style={{ position: "relative", flexShrink: 0, display: "flex" }}>
        {avatarSlot ?? <Avatar name={name} avatarUrl={avatarUrl} size={avatarSize} />}
        {frame && <FrameOverlay frame={frame} avatarSize={avatarSize} />}
      </div>
      <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
        {titleText && (
          <span
            className="eyebrow"
            style={{
              fontSize: size === "lg" ? 11 : 10,
              color: titleColorVar(title?.metadata.colorToken, title?.metadata.colorHex),
              lineHeight: 1,
            }}
          >
            {titleText}
          </span>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <span
            style={{
              font: size === "lg" ? "var(--type-h2)" : "var(--type-h3)",
              fontSize: size === "sm" ? 15 : undefined,
              fontWeight: size === "sm" ? 600 : undefined,
              color: "var(--text-primary)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {name}
          </span>
          {badges.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
              {badges.slice(0, 3).map((b) => (
                <BadgeChip key={b.itemId} badge={b} size={badgeSize} />
              ))}
            </div>
          )}
        </div>
        {subtitle}
      </div>
    </div>
  );
}
